import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { Router } from "@angular/router";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faDownload, faPencil, faPlus } from '@fortawesome/free-solid-svg-icons';
import { ApiService } from '../services/api.service';
import { Filled, Project, Questionnaire } from '../models';
import { RouterLink } from "@angular/router";
import { QuestionnaireStatusComponent } from "../questionnaire-status/questionnaire-status";

type DashboardProject = Filled<Project, 'questionnaire', Questionnaire> & { expanded: boolean };

function byDate(key: 'date_started' | 'date_edited') {
    return (a: Questionnaire, b: Questionnaire) =>
        a[key] > b[key]
            ? 1
            : a[key] === b[key]
                ? 0
                : -1
}

function revSort<T>(compareFn: (a: T, b: T) => 1 | 0 | -1) {
    return (a: T, b: T) => {
        switch (compareFn(a, b)) {
            case 0:
                return 0;

            case 1:
                return -1;

            case -1:
                return 1;
        }
    }
}

@Component({
    selector: 'fgai-dashboard',
    imports: [CommonModule, FontAwesomeModule, RouterLink, QuestionnaireStatusComponent],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
    faDownload = faDownload;
    faPencil = faPencil;
    faPlus = faPlus;

    projects = signal<DashboardProject[]>([]);

    constructor(private apiService: ApiService, private router: Router) {
        this.retrieve();
    }

    private async retrieve() {
        const projects = await this.apiService.projects.list();
        const dashboardProjects: DashboardProject[] = await Promise.all(projects.map(async project => ({
            ...project,
            expanded: false,
            questionnaire: (await Promise.all(
                project.questionnaire.map(async q =>
                    // the oldest questionnaires are first
                    await this.apiService.questionnaires.get(q)))).sort(byDate('date_started'))
        })));

        this.projects.set(dashboardProjects);
    }

    toggle(project: DashboardProject) {
        if (!project.questionnaire.length) {
            // empty projects cannot be toggled
            return;
        }
        project.expanded = !project.expanded;
        this.projects.update(p => [...p]);
    }

    latestQuestionnaire(project: DashboardProject) {
        return [...project.questionnaire].sort(revSort(byDate('date_edited')))[0];
    }

    download(q: Questionnaire) {
        console.log('Downloading questionnaire', q.id);
    }

    async startNewQuestionnaire(projectId: string, first: boolean) {
        const questionnaire = await this.apiService.createFilledQuestionnaire(projectId);
        questionnaire.project = projectId;
        this.router.navigate([`questionnaire/${questionnaire.id}/edit`], {
            queryParams: {
                hide_diff: first ? '1' : undefined
            }
        });
    }
}
